import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
const HomeV2 = lazy(() => import("./pages/HomeV2"));
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import FloatingCallCTA from "./components/FloatingCallCTA";

import DeferredChat from "./components/DeferredChat";

// Lazy-loaded routes — keeps homepage JS minimal and defers per-route code.
const NotFound = lazy(() => import("./pages/NotFound"));
const MarketingStrategyPage = lazy(() => import("./pages/MarketingStrategyPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ContentCreationPage = lazy(() => import("./pages/ContentCreationPage"));
const SEOTreasureCoastPage = lazy(() => import("./pages/SEOTreasureCoastPage"));
const CommercialPhotoVideoPage = lazy(() => import("./pages/CommercialPhotoVideoPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const InsightsPage = lazy(() => import("./pages/InsightsPage"));
const BlogArticleRoute = lazy(() => import("./pages/BlogArticleRoute"));
const DisclaimerPage = lazy(() => import("./pages/DisclaimerPage"));
const DentalMarketingPage = lazy(() => import("./pages/DentalMarketingPage"));
const MedicalMarketingPage = lazy(() => import("./pages/MedicalMarketingPage"));
// HybridMedicalMarketingPage retired — route now redirects to /medical-marketing-port-st-lucie
const LocalBusinessMarketingPage = lazy(() => import("./pages/LocalBusinessMarketingPage"));
const CommercialPage = lazy(() => import("./pages/CommercialPage"));
const LocationsPage = lazy(() => import("./pages/LocationsPage"));
// Bespoke city pages (Phase 1)
const FortPiercePage = lazy(() => import("./pages/cities/FortPiercePage"));
const StuartPage = lazy(() => import("./pages/cities/StuartPage"));
const PortStLuciePage = lazy(() => import("./pages/cities/PortStLuciePage"));
const TraditionPage = lazy(() => import("./pages/cities/TraditionPage"));
const VeroBeachPage = lazy(() => import("./pages/cities/VeroBeachPage"));
// Bespoke city pages (Phase 2)
const JensenBeachPage = lazy(() => import("./pages/cities/JensenBeachPage"));
const PalmCityPage = lazy(() => import("./pages/cities/PalmCityPage"));
const HobeSoundPage = lazy(() => import("./pages/cities/HobeSoundPage"));
const JupiterPage = lazy(() => import("./pages/cities/JupiterPage"));
const PalmBeachGardensPage = lazy(() => import("./pages/cities/PalmBeachGardensPage"));
const MelbournePage = lazy(() => import("./pages/cities/MelbournePage"));
// Phase 3 — remaining cities
const SebastianPage = lazy(() => import("./pages/cities/SebastianPage"));
const WhiteCityPage = lazy(() => import("./pages/cities/WhiteCityPage"));
const RiverParkPage = lazy(() => import("./pages/cities/RiverParkPage"));
const StLucieWestPage = lazy(() => import("./pages/cities/StLucieWestPage"));
const SewallsPointPage = lazy(() => import("./pages/cities/SewallsPointPage"));
const PortSalernoPage = lazy(() => import("./pages/cities/PortSalernoPage"));
const IndiantownPage = lazy(() => import("./pages/cities/IndiantownPage"));
const JupiterIslandPage = lazy(() => import("./pages/cities/JupiterIslandPage"));
const OkeechobeePage = lazy(() => import("./pages/cities/OkeechobeePage"));
const TequestaPage = lazy(() => import("./pages/cities/TequestaPage"));
const JunoBeachPage = lazy(() => import("./pages/cities/JunoBeachPage"));
const RivieraBeachPage = lazy(() => import("./pages/cities/RivieraBeachPage"));
const WestPalmBeachPage = lazy(() => import("./pages/cities/WestPalmBeachPage"));
const TitusvillePage = lazy(() => import("./pages/cities/TitusvillePage"));
const MimsPage = lazy(() => import("./pages/cities/MimsPage"));
const PortStJohnPage = lazy(() => import("./pages/cities/PortStJohnPage"));
const CocoaPage = lazy(() => import("./pages/cities/CocoaPage"));
const CocoaBeachPage = lazy(() => import("./pages/cities/CocoaBeachPage"));
const RockledgePage = lazy(() => import("./pages/cities/RockledgePage"));
const MerrittIslandPage = lazy(() => import("./pages/cities/MerrittIslandPage"));
const CapeCanaveralPage = lazy(() => import("./pages/cities/CapeCanaveralPage"));
const VieraPage = lazy(() => import("./pages/cities/VieraPage"));
const SuntreePage = lazy(() => import("./pages/cities/SuntreePage"));
const SatelliteBeachPage = lazy(() => import("./pages/cities/SatelliteBeachPage"));
const IndianHarbourBeachPage = lazy(() => import("./pages/cities/IndianHarbourBeachPage"));
const IndialanticPage = lazy(() => import("./pages/cities/IndialanticPage"));
const MelbourneBeachPage = lazy(() => import("./pages/cities/MelbourneBeachPage"));
const WestMelbournePage = lazy(() => import("./pages/cities/WestMelbournePage"));
const PalmBayPage = lazy(() => import("./pages/cities/PalmBayPage"));
const MalabarPage = lazy(() => import("./pages/cities/MalabarPage"));
const GrantValkariaPage = lazy(() => import("./pages/cities/GrantValkariaPage"));
const MiccoPage = lazy(() => import("./pages/cities/MiccoPage"));
const BlogMarketingAgencyPSL = lazy(() => import("./pages/BlogMarketingAgencyPSL"));
const BlogNewDentistsBuildingPractice = lazy(() => import("./pages/BlogNewDentistsBuildingPractice"));
const BlogDentalMarketingVisibilityTrust = lazy(() => import("./pages/BlogDentalMarketingVisibilityTrust"));
const BlogDentalMarketingLeadsToScheduledPatients = lazy(() => import("./pages/BlogDentalMarketingLeadsToScheduledPatients"));
const SouthEastDentalCaseStudy = lazy(() => import("./pages/SouthEastDentalCaseStudy"));
const HealthcareMarketingHub = lazy(() => import("./pages/healthcare/HealthcareMarketingHub"));
const HealthcareMarketingCareers = lazy(() => import("./pages/healthcare/HealthcareMarketingCareers"));
const HealthcareMarketingTreasureCoast = lazy(() => import("./pages/healthcare/HealthcareMarketingTreasureCoast"));
const DentalMarketingStuart = lazy(() => import("./pages/healthcare/DentalMarketingStuart"));
const MedicalMarketingStuart = lazy(() => import("./pages/healthcare/MedicalMarketingStuart"));
const GoogleMapsOptimizationPSL = lazy(() => import("./pages/healthcare/GoogleMapsOptimizationPSL"));
const PatientGrowthMarketing = lazy(() => import("./pages/healthcare/PatientGrowthMarketing"));
const HealthcareSEOServices = lazy(() => import("./pages/healthcare/HealthcareSEOServices"));
const DentalMarketingAgencyFlorida = lazy(() => import("./pages/healthcare/DentalMarketingAgencyFlorida"));
const GoogleBusinessProfileForDentists = lazy(() => import("./pages/healthcare/GoogleBusinessProfileForDentists"));
const DentalVideoMarketing = lazy(() => import("./pages/healthcare/DentalVideoMarketing"));
const DentalPatientConversionSystem = lazy(() => import("./pages/healthcare/DentalPatientConversionSystem"));

const queryClient = new QueryClient();

// All cities now have bespoke pages — no pending redirects.

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/home-v2" element={<HomeV2 />} />

            {/* Service-area hub */}
            <Route path="/locations" element={<LocationsPage />} />

            {/* Bespoke city pages (Phase 1 + Phase 2) */}
            <Route path="/marketing-fort-pierce" element={<FortPiercePage />} />
            <Route path="/marketing-stuart" element={<StuartPage />} />
            <Route path="/marketing-port-st-lucie" element={<PortStLuciePage />} />
            <Route path="/marketing-tradition" element={<TraditionPage />} />
            <Route path="/marketing-vero-beach" element={<VeroBeachPage />} />
            <Route path="/marketing-jensen-beach" element={<JensenBeachPage />} />
            <Route path="/marketing-palm-city" element={<PalmCityPage />} />
            <Route path="/marketing-hobe-sound" element={<HobeSoundPage />} />
            <Route path="/marketing-jupiter" element={<JupiterPage />} />
            <Route path="/marketing-palm-beach-gardens" element={<PalmBeachGardensPage />} />
            <Route path="/marketing-melbourne" element={<MelbournePage />} />

            {/* Phase 3 — all remaining cities */}
            <Route path="/marketing-sebastian" element={<SebastianPage />} />
            <Route path="/marketing-white-city" element={<WhiteCityPage />} />
            <Route path="/marketing-river-park" element={<RiverParkPage />} />
            <Route path="/marketing-st-lucie-west" element={<StLucieWestPage />} />
            <Route path="/marketing-sewalls-point" element={<SewallsPointPage />} />
            <Route path="/marketing-port-salerno" element={<PortSalernoPage />} />
            <Route path="/marketing-indiantown" element={<IndiantownPage />} />
            <Route path="/marketing-jupiter-island" element={<JupiterIslandPage />} />
            <Route path="/marketing-okeechobee" element={<OkeechobeePage />} />
            <Route path="/marketing-tequesta" element={<TequestaPage />} />
            <Route path="/marketing-juno-beach" element={<JunoBeachPage />} />
            <Route path="/marketing-riviera-beach" element={<RivieraBeachPage />} />
            <Route path="/marketing-west-palm-beach" element={<WestPalmBeachPage />} />
            <Route path="/marketing-titusville" element={<TitusvillePage />} />
            <Route path="/marketing-mims" element={<MimsPage />} />
            <Route path="/marketing-port-st-john" element={<PortStJohnPage />} />
            <Route path="/marketing-cocoa" element={<CocoaPage />} />
            <Route path="/marketing-cocoa-beach" element={<CocoaBeachPage />} />
            <Route path="/marketing-rockledge" element={<RockledgePage />} />
            <Route path="/marketing-merritt-island" element={<MerrittIslandPage />} />
            <Route path="/marketing-cape-canaveral" element={<CapeCanaveralPage />} />
            <Route path="/marketing-viera" element={<VieraPage />} />
            <Route path="/marketing-suntree" element={<SuntreePage />} />
            <Route path="/marketing-satellite-beach" element={<SatelliteBeachPage />} />
            <Route path="/marketing-indian-harbour-beach" element={<IndianHarbourBeachPage />} />
            <Route path="/marketing-indialantic" element={<IndialanticPage />} />
            <Route path="/marketing-melbourne-beach" element={<MelbourneBeachPage />} />
            <Route path="/marketing-west-melbourne" element={<WestMelbournePage />} />
            <Route path="/marketing-palm-bay" element={<PalmBayPage />} />
            <Route path="/marketing-malabar" element={<MalabarPage />} />
            <Route path="/marketing-grant-valkaria" element={<GrantValkariaPage />} />
            <Route path="/marketing-micco" element={<MiccoPage />} />

            {/* Existing service & vertical pages (legacy slugs) */}
            {/* Healthcare authority hub + audience paths */}
            <Route path="/healthcare-marketing" element={<HealthcareMarketingHub />} />
            <Route path="/healthcare-marketing-florida" element={<Navigate to="/healthcare-marketing" replace />} />
            <Route path="/healthcare-marketing-treasure-coast" element={<HealthcareMarketingTreasureCoast />} />
            <Route path="/healthcare-marketing-careers" element={<HealthcareMarketingCareers />} />

            {/* Healthcare service pages (Wave 2) */}
            <Route path="/dental-marketing-stuart-fl" element={<DentalMarketingStuart />} />
            <Route path="/medical-marketing-stuart-fl" element={<MedicalMarketingStuart />} />
            <Route path="/google-maps-optimization-port-st-lucie" element={<GoogleMapsOptimizationPSL />} />
            <Route path="/patient-growth-marketing" element={<PatientGrowthMarketing />} />
            <Route path="/healthcare-seo-services" element={<HealthcareSEOServices />} />
            <Route path="/dental-marketing-agency-florida" element={<DentalMarketingAgencyFlorida />} />
            <Route path="/google-business-profile-for-dentists" element={<GoogleBusinessProfileForDentists />} />
            <Route path="/dental-video-marketing" element={<DentalVideoMarketing />} />
            <Route path="/dental-patient-conversion-system" element={<DentalPatientConversionSystem />} />

            <Route path="/dental-marketing-port-st-lucie" element={<DentalMarketingPage />} />
            <Route path="/medical-marketing-port-st-lucie" element={<MedicalMarketingPage />} />
            <Route path="/south-east-dental-case-study" element={<SouthEastDentalCaseStudy />} />
            {/* Retired duplicate: hybrid medical page now redirects to the canonical medical marketing page */}
            <Route path="/hybrid-medical-marketing-port-st-lucie" element={<Navigate to="/medical-marketing-port-st-lucie" replace />} />
            <Route path="/local-business-marketing-port-st-lucie" element={<LocalBusinessMarketingPage />} />
            <Route path="/marketing-strategy" element={<MarketingStrategyPage />} />
            <Route path="/services" element={<ServicesPage />} />

            <Route path="/content-creation-stuart" element={<ContentCreationPage />} />
            <Route path="/seo-treasure-coast" element={<SEOTreasureCoastPage />} />
            <Route path="/commercial-photo-video-florida" element={<CommercialPhotoVideoPage />} />
            <Route path="/commercial" element={<CommercialPage />} />
            <Route path="/event-photography-florida" element={<EventsPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:slug" element={<BlogArticleRoute />} />
            <Route path="/blog/marketing-agency-port-st-lucie" element={<BlogMarketingAgencyPSL />} />
            <Route path="/blog/new-dentists-building-a-practice" element={<BlogNewDentistsBuildingPractice />} />
            <Route path="/blog/dental-marketing-visibility-trust-plan" element={<BlogDentalMarketingVisibilityTrust />} />
            <Route path="/blog/dental-marketing-leads-to-scheduled-patients" element={<BlogDentalMarketingLeadsToScheduledPatients />} />
            <Route path="/blog" element={<InsightsPage />} />
            <Route path="/blog/:slug" element={<BlogArticleRoute />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <DeferredChat />
        <FloatingCallCTA />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
